import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useParams } from "react-router-dom";
import React, { Component, lazy, Suspense, useEffect, useRef, useState } from "react";
import MainPageWithParams from './MainPage';
import AppTheme from "../src/shared-theme/AppTheme.js";
import 'mapbox-gl/dist/mapbox-gl.css';
import SignIn from './SignIn.js';
import Q2HighlightsPage from "./highlights/Q2HighlightsPage";
import TaxCaseStudyPage from "./highlights/TaxCaseStudyPage";
import { CssBaseline } from "@mui/material";
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from "../src/theme/customizations";
import { SelectionProvider } from "./state/SelectionContext";
import { isMobileDevice } from "./utils/mobileDetect";
import ReportRenderPage from "./ReportRenderPage";

const MobileApp = lazy(() => import("./mobile/MobileApp"));

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

/**
 * MobileRedirect — fires once on app mount.
 *
 * When the viewport is mobile-width (<900px) AND the user is confirmed logged-in,
 * redirects to /m — UNLESS they have explicitly opted into the desktop experience
 * (preferDesktop localStorage flag) or are already on a mobile/admin/render route.
 *
 * WHY auth-gated (not width-only):
 *   After logout, window.location.href = "/" causes a full remount of App.
 *   A width-only redirect would immediately bounce the user back to /m even though
 *   they just logged out and should see the login page. Gating on /api/user-info/
 *   (401 = logged-out → no redirect; 200 = logged-in → redirect) fixes this:
 *   logged-out users stay on login regardless of which route they land on.
 *   The fetch is the same call SignIn.js, MobileRoot, and MainPage all make.
 *
 * Breakpoint 900px: below this the desktop sidebar + map layout is unusable.
 *
 * Hard-excluded from redirect (no fetch even attempted):
 *   /m, /m/*         — already on mobile, prevents loops
 *   /admin/*         — Django admin
 *   /report-render   — headless Chromium PDF target
 *   preferDesktop    — user's explicit "keep me on desktop" choice
 *
 * /highlights/* cold links → fetch 200 (logged-in) → redirect to /m ✓
 *   (In-app navigate from HighlightsScreen doesn't remount App, effect won't re-fire — no loop.)
 *
 * navigate(..., {replace:true}) so back-button doesn't land on the pre-redirect route.
 */
// isMobileDevice imported from utils/mobileDetect.js — shared with Q2HighlightsPage CTA.

function MobileRedirect() {
  const location = useLocation();

  // In-memory guard (useRef — not persisted storage, resets on every page load).
  // Purpose: the effect uses location.pathname as dep so it re-fires after SignIn's
  // in-app navigate('/explore') post-login.  Without this guard, navigating from /m
  // to /highlights/q2-2026 would also fire and redirect back → loop.
  // After window.location.replace("/m") the page hard-reloads; the app remounts at
  // /m; sessionStartedAtMobile becomes true; all subsequent in-app navigations
  // return early without redirecting.
  const sessionStartedAtMobile = useRef(window.location.pathname.startsWith("/m"));

  useEffect(() => {
    if (sessionStartedAtMobile.current) return;

    const path = location.pathname;
    if (path.startsWith("/m")) return;
    if (path.startsWith("/admin") || path.startsWith("/report-render")) return;
    if (localStorage.getItem("preferDesktop") === "true") return;
    if (!isMobileDevice()) return;

    // Auth-gated: only redirect logged-in users so logout → login stays on login page.
    fetch("/api/user-info/", { credentials: "include" })
      .then(r => { if (r.ok) window.location.replace("/m"); })
      .catch(() => {});
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

// ── MobileSwitchPopup ─────────────────────────────────────────────────────────
// Re-offers the mobile experience to users who previously chose "View desktop site".
//
// Shows ONLY when ALL of:
//   • isMobileDevice() — this is a mobile-width screen
//   • preferDesktop = "true" in localStorage — user previously opted into desktop
//   • path === "/explore" — only on the main page, not sub-pages or /m
//
// Dismiss is in-memory (useState). Dismissing hides it for this page-load.
// It re-appears on the next fresh /explore load — re-offered each visit without nagging
// mid-session. Not persisted: sessionStorage/localStorage are intentionally NOT used.
//
// "Switch" clears preferDesktop so future visits auto-redirect again, then goes to /m.
function MobileSwitchPopup() {
  const location  = useLocation();
  const [dismissed, setDismissed] = useState(false);

  const shouldShow =
    !dismissed &&
    location.pathname === "/explore" &&
    localStorage.getItem("preferDesktop") === "true" &&
    isMobileDevice();

  if (!shouldShow) return null;

  const handleSwitch = () => {
    localStorage.removeItem("preferDesktop");
    window.location.replace("/m");
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Switch to mobile site"
      style={{
        position: "fixed",
        bottom: 0, left: 0, right: 0,
        zIndex: 9999,
        background: "#fff",
        borderTop: "1px solid rgba(0,0,0,0.10)",
        boxShadow: "0 -4px 24px rgba(0,0,0,0.13)",
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "14px 16px",
        paddingBottom: "calc(14px + env(safe-area-inset-bottom))",
      }}
    >
      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 2 }}>
          Better on mobile
        </div>
        <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.4 }}>
          Switch to the mobile version of this site.
        </div>
      </div>

      {/* Switch button */}
      <button
        onClick={handleSwitch}
        style={{
          flexShrink: 0,
          background: "#0f9fab",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          padding: "10px 18px",
          fontSize: 13,
          fontWeight: 700,
          cursor: "pointer",
          whiteSpace: "nowrap",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        Switch
      </button>

      {/* Dismiss */}
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        style={{
          flexShrink: 0,
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#9ca3af",
          padding: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>
  );
}

function MainPageWithParamsWrapper() {
  const { slug } = useParams();
  // FIX: Removed key={slug}. 
  // Now the component updates instead of reloading, enabling seamless transitions.
  return <MainPageWithParams slug={slug} />;
}

export default class App extends Component {
  render() {
    return (
      <AppTheme {...this.props} disableCustomTheme={false} themeComponents={xThemeComponents}>
        <CssBaseline />
        <SelectionProvider>
        <Router>
          <MobileRedirect />
          <MobileSwitchPopup />
          <Routes>
            <Route path="/" element={<SignIn />} />

            {/* ── Highlights / scrollytelling experiences ─────────────────── */}
            {/* Must be registered BEFORE /:slug so they aren't captured by it */}
            <Route path="/highlights/q2-2026" element={<Q2HighlightsPage />} />
            <Route path="/highlights/tax-case-study" element={<TaxCaseStudyPage />} />

            {/* ❗ DO NOT LET React HANDLE /admin */}
            <Route path="/admin/*" element={<div>Admin lives on Django — not React.</div>} />

            {/* ── Mobile shell (opt-in, lazy-loaded) ──────────────────────── */}
            {/* Must be registered BEFORE /:slug so /m isn't captured by it */}
            <Route
              path="/m"
              element={<Suspense fallback={<div>Loading…</div>}><MobileApp /></Suspense>}
            />

            {/* ── Standalone report render — for headless Chromium (Step B) ── */}
            {/* Must be registered BEFORE /:slug so it isn't captured by it  */}
            <Route path="/report-render" element={<ReportRenderPage />} />

            {/* Dynamic slug handling */}
            <Route
              path="/:slug"
              element={<MainPageWithParamsWrapper />}
            />
          </Routes>
        </Router>
        </SelectionProvider>
      </AppTheme>
    );
  }
}